import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { CountrySelect } from "@/components/ui/country-select";
import { UserApi } from "@/api/user.api";
import { User } from "@/types/user";

const formSchema = z.object({
  firstName: z.string().min(1, {
    message: "Required",
  }),
  lastName: z.string().min(1, {
    message: "Required",
  }),
  countryCode: z.string().default("+971"),
  mobile: z.string().optional(),
});

type EditUserFormValues = z.infer<typeof formSchema>;

interface EditUserSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onUserUpdated?: (updatedUser: User) => void;
}

export function EditUserSheet({
  open,
  onOpenChange,
  user,
  onUserUpdated,
}: EditUserSheetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<EditUserFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      countryCode: "+971",
      mobile: "",
    },
  });

  // Reset form when user changes or sheet opens
  useEffect(() => {
    if (open && user) {
      // Split the name into first and last name
      const nameParts = user.name.split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      form.reset({
        firstName,
        lastName,
        countryCode: user.phoneCountryCode || "+971", // Use phoneCountryCode from user data
        mobile: user.phoneNumber || "", // Use phoneNumber from user data
      });
    }
  }, [open, user, form]);

  async function onSubmit(values: EditUserFormValues) {
    if (isSubmitting || !user) return;
    setIsSubmitting(true);

    try {
      const userVO: any = {
        id: parseInt(user.id), // Send the user ID for update
        username: user.username, // Keep existing username/email
        name: `${values.firstName} ${values.lastName}`,
        phoneCountryCode: values.countryCode,
        phoneNumber: values.mobile || "",
        // Keep existing role and other fields
        roleVO: {
          id: user.roleVO?.id || 0, // Use the role id from API response
          roleName:
            user.roleVO?.roleName ||
            user.role.charAt(0).toUpperCase() + user.role.slice(1), // Use roleName from API or capitalize role code
        },
        mfaEnabled: false, // This should be preserved from existing user data
      };

      const response = await UserApi.createOrUpdateUser({
        userVO,
      });

      if (response.data) {
        toast.success("User updated successfully");

        const updatedUser: User = {
          ...user,
          name: `${values.firstName} ${values.lastName}`,
        };

        if (onUserUpdated) {
          onUserUpdated(updatedUser);
        }

        onOpenChange(false);
      }
    } catch (error: any) {
      toast.error("Failed to update user");
      console.error("Update user error:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Edit User</SheetTitle>
          <SheetDescription>
            Update user information. Click save when you're done.
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter first name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter last name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="countryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country Code</FormLabel>
                    <FormControl>
                      <CountrySelect
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter mobile number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update User"}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
